/* Notification Center — Rule Detail page, ported from the standalone HTML
 * prototype's #page-rule-detail.  Opened by Edit (1 row selected) or by clicking
 * a rule name on the Notification Rules list.  8 tabs: General · Trigger
 * Condition · Targets · Recipients · Delivery Method · Escalation · Email
 * Template · Audit/History.  Demo data — the conditions are built-in MDI
 * defaults; the one configurable thing is recipients.
 *
 * TARGETS tab (2026-09-19 redesign): a master/detail expandable grid driven by
 * the Permit Library (permitLibrary.ts) — the source of truth.  One main row per
 * parameter/analyte per permit (never duplicated for its multiple limit types),
 * expanding to a sub-grid of that parameter's configured limits.  No limit value
 * is entered here; everything resolves from the Permit Library. */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useAuthStore } from '../../auth/store';
import { fmtLocalShort } from '../../utils/datetime';
import {
  PURPOSE_LABEL, SEVERITY_LABEL,
  parseRuleConfig, recipientGroupIdsOf, groupRefsFor, emailTemplateIdOf,
  type NcRule, type Severity, type NcAuditEntry, type NcRuleConfig, type NcDelayMode, type NcEscalateAfter,
} from './data';
import { getHoldTimeCatalog, getPermitLibrary } from './api';
import { buildPermitTargets, type PlPermit } from './permitLibrary';
import { PermitTargetsGrid } from './PermitTargetsGrid';
import { HoldTimeTargetsGrid } from './HoldTimeTargetsGrid';
import {
  describeTarget, fmtWarnings, holdTimeTargetsError, htKey, isHoldTimeCategory, selFromTargets, targetLabel, toSavedTargets,
  type HoldTimeCatalogRow, type HoldTimeSel,
} from './holdTime';
import { RecipientGroupPicker } from './RecipientGroupPicker';
import { EmailTemplatePicker } from './EmailTemplatePicker';
import { enabledTriggerForms, triggerFormById, triggerLabels, triggersEvaluationText } from './triggerForms';
import { useNcStore } from './store';
import { describeAlertPopup, type AlertPopupSettings } from './inbox/alertPopupSettings';
import { listPopupSettings, type PopupSetting } from './inbox/inboxApi';

const SEVERITY_OPTIONS: Severity[] = ['critical', 'warning', 'info'];

type Tab = 'general' | 'trigger' | 'targets' | 'recipients' | 'delivery' | 'escalation' | 'template' | 'audit';
const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'general', icon: 'ti-info-circle', label: 'General' },
  { key: 'trigger', icon: 'ti-bolt', label: 'Trigger Condition' },
  { key: 'targets', icon: 'ti-target', label: 'Targets' },
  { key: 'recipients', icon: 'ti-users-group', label: 'Recipients' },
  { key: 'delivery', icon: 'ti-send', label: 'Delivery Method' },
  { key: 'escalation', icon: 'ti-arrow-up-circle', label: 'Escalation' },
  { key: 'template', icon: 'ti-mail', label: 'Email Template' },
  { key: 'audit', icon: 'ti-history', label: 'Audit / History' },
];

function SevPill({ s }: { s: Severity }) {
  const label = s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';
  return <span className={`nc-sev ${s}`}><span className="dot" />{label}</span>;
}
function Info({ label, children }: { label: string; children: ReactNode }) {
  return <div className="nc-info-item"><div className="nc-ii-label">{label}</div><div className="nc-ii-value">{children}</div></div>;
}
/** The Result Exceedance (permit-limit) category's code — the only category Suppress duplicate firing
 *  applies to (owner decision 2026-10-19, hardcoded by code). */
const PERMIT_EXCEEDANCE_CATEGORY_CODE = 'RC-01';

/** Repeat-interval unit (owner request 2026-10-18: Hours or Minutes). */
type RepeatUnit = 'Hours' | 'Minutes';
const REPEAT_UNITS: RepeatUnit[] = ['Minutes', 'Hours'];

/** Hours ⇄ Minutes spinner — the Hold Time Warn Before spinner's look (.nc-wb-spin): ▲/▼ buttons or ↑/↓ keys. */
function UnitSpin({ value, onChange, disabled }: { value: RepeatUnit; onChange: (u: RepeatUnit) => void; disabled?: boolean }) {
  const step = (dir: 1 | -1) => {
    if (disabled) return;
    const i = REPEAT_UNITS.indexOf(value);
    onChange(REPEAT_UNITS[(i + dir + REPEAT_UNITS.length) % REPEAT_UNITS.length]!);
  };
  return (
    <div className={`nc-wb-spin nc-repeat-spin${disabled ? ' is-disabled' : ''}`} role="spinbutton" tabIndex={disabled ? -1 : 0}
      aria-label="Repeat unit" aria-valuetext={value} aria-valuenow={REPEAT_UNITS.indexOf(value)}
      aria-valuemin={0} aria-valuemax={REPEAT_UNITS.length - 1} aria-disabled={disabled || undefined}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); step(-1); }
      }}>
      <span className="nc-wb-unit">{value}</span>
      <span className="nc-wb-spin-btns">
        <button type="button" tabIndex={-1} aria-label="Next repeat unit" disabled={disabled} onClick={() => step(1)}>▲</button>
        <button type="button" tabIndex={-1} aria-label="Previous repeat unit" disabled={disabled} onClick={() => step(-1)}>▼</button>
      </span>
    </div>
  );
}

/** Repeat until acknowledged: why the interval is not usable, or null (the server's RepeatPolicy rule:
 *  a whole number 1–999, at least 5 minutes). */
function repeatIntervalError(r: { value: string; unit: RepeatUnit }): string | null {
  const n = Number(r.value);
  if (!/^\d+$/.test(r.value) || n < 1 || n > 999) return 'Enter a whole number from 1 to 999.';
  if (r.unit === 'Minutes' && n < 5) return 'The shortest repeat is 5 minutes.';
  return null;
}
const repeatLabel = (r: { on: boolean; value: string; unit: RepeatUnit }) => (r.on ? `Every ${r.value} ${r.unit}` : 'Off');

/** "Escalate after" as shown ⇄ as stored (AI_NotificationRule.EscalateAfter). */
const ESCALATE_AFTER_LABEL: Record<NcEscalateAfter, string> = {
  '30m': '30 minutes', '1h': '1 hour', '4h': '4 hours', EOD: 'End of business day (17:00)',
};

/** One line under the Escalation fields saying what the saved settings will do. */
function escNote(esc: { groupId: string; after: NcEscalateAfter }, bizHours: boolean): string {
  if (!esc.groupId) return 'No escalation: an unacknowledged alert stays with the rule\'s own recipients.';
  const when = esc.after === 'EOD'
    ? `at 17:00 on the day it was sent (the next${bizHours ? ' working' : ''} day once 17:00 has passed)`
    : `${ESCALATE_AFTER_LABEL[esc.after]} after it was sent${bizHours ? ' (moved to the next opening outside business hours)' : ''}`;
  return `If nobody has acknowledged the alert ${when}, it is sent once to this group as “Escalated: …” (In-App, and Email when ticked). Any acknowledgment stops it.`;
}

/** Delay as shown in the dropdown ⇄ as stored (AI_NotificationRule.DelayMode). */
type DelayLabel = 'Immediate' | 'After 1 hour' | 'Daily digest';
const DELAY_LABEL: Record<NcDelayMode, DelayLabel> = { Immediate: 'Immediate', After1Hour: 'After 1 hour', DailyDigest: 'Daily digest' };
const DELAY_STORED: Record<DelayLabel, NcDelayMode> = { Immediate: 'Immediate', 'After 1 hour': 'After1Hour', 'Daily digest': 'DailyDigest' };
const bizHoursLabel = (only: boolean) => (only ? 'Business hours only (Mon–Fri 08:00–17:00, not on lab holidays)' : 'Send any time (24/7)');

/** One line under Delay / Business hours saying what will actually happen (the server decides — this only
 *  describes the saved settings). */
function delayNote(delay: DelayLabel, bizHours: boolean): string {
  const hours = bizHours ? ' An alert due outside business hours waits for the next opening (08:00 on a working day).' : '';
  if (delay === 'After 1 hour')
    return `Held for one hour, then re-checked: only what still meets the condition is sent.${hours}`;
  if (delay === 'Daily digest')
    return `Held alerts go out together at 08:00 as one email and one bell entry for this rule, re-checked first.${bizHours ? ' On weekends and lab holidays the digest waits for the next working day.' : ''}`;
  return bizHours ? `Sent as soon as raised during business hours.${hours}` : 'Sent as soon as the alert is raised.';
}

function ToggleRow({ text, sub, on, onClick, disabled, title }: { text: string; sub: string; on: boolean; onClick: () => void; disabled?: boolean; title?: string }) {
  return <div className="nc-togrow" title={title}><div><div className="nc-togrow-text">{text}</div><div className="nc-togrow-sub">{sub}</div></div><span className={`nc-toggle${on ? ' on' : ''}${disabled ? ' nc-ro' : ''}`} role="button" aria-label={text} aria-disabled={disabled || undefined} onClick={disabled ? undefined : onClick} /></div>;
}

export default function RuleDetailPage({ rule, categoryName, onBack, onDeleted, initialMode = 'view' }: {
  rule: NcRule; categoryName: string; onBack: () => void; onDeleted: () => void;
  /** 'edit' = the list toolbar Edit opened the rule: start in Edit mode (RTCFR 2026-10-16).
   *  'view' (default) = a click on the rule name. Read on open only. */
  initialMode?: 'view' | 'edit';
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const patchRule = useNcStore((s) => s.patchRule);
  const liveGroups = useNcStore((s) => s.recipients);
  const liveTemplates = useNcStore((s) => s.templates);
  const meName = useAuthStore((s) => s.user?.name || s.user?.email || 'System');
  const [tab, setTab] = useState<Tab>('general');
  const [active, setActive] = useState(rule.enabled);
  // Editable header/general fields (persisted columns) + config-backed fields.
  const [name, setName] = useState(rule.name);
  const [severity, setSeverity] = useState<Severity | undefined>(rule.severity);
  // Recipients — predefined Recipient GROUPS (not ad-hoc roles). This holds the
  // selected group IDS (the stable link — dbo.AI_NotificationRuleRecipientGroup);
  // every name shown is looked up LIVE by id, so a renamed group shows its new
  // name here and in the picker, never "Group no longer exists".
  const [recipientGroups, setRecipientGroups] = useState<string[]>(() => recipientGroupIdsOf(rule, useNcStore.getState().recipients));
  /** Current display names for a set of group ids (deleted → last-known name). */
  const groupNames = (ids: string[]) => groupRefsFor(ids, liveGroups, rule.recipientGroups).map((g) => g.name || 'Unknown group');
  // Email Template — the linked template's ID (AI_NotificationRule.EmailTemplateId),
  // a reference, never a copy; null = no template (built-in email). Its name is
  // always looked up LIVE by id, so a renamed template shows its new name.
  const [emailTemplate, setEmailTemplate] = useState<string | null>(() => emailTemplateIdOf(rule, useNcStore.getState().templates));
  /** Current display name for a template id (for the audit history). */
  const templateName = (id: string | null) =>
    !id ? 'None (built-in email)' : (liveTemplates.find((t) => t.id === id)?.name ?? 'Deleted template');
  // Audit / History — the rule's REAL persisted change log (config.history);
  // never seeded. Empty until a Save records an actual diff.
  const [history, setHistory] = useState<NcAuditEntry[]>(() => parseRuleConfig(rule).history ?? []);
  // TARGETS — the FULL live Permit Library (source of truth) fetched from the API,
  // one row per parameter/permit. EVERY configured parameter is listed (not just a
  // rule's selected ones); the rule owns only the per-parameter Enabled toggle and
  // which rows are expanded — the limits themselves are never edited here.
  const [library, setLibrary] = useState<PlPermit[] | null>(null);
  const targetsLoading = library === null;
  useEffect(() => {
    let alive = true;
    getPermitLibrary().then((l) => { if (alive) setLibrary(l); }).catch(() => { if (alive) setLibrary([]); });
    return () => { alive = false; };
  }, []);
  const permitTargets = useMemo(() => buildPermitTargets(library ?? []), [library]);
  // The rule's SAVED ON/OFF set (rule.targetKeys), seeded once the library loads.
  // A rule WITH saved keys shows exactly those ON and every other parameter OFF;
  // a legacy rule with none defaults every parameter ON. Never resets a selection.
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (library === null) return;
    const seeded = Object.fromEntries(permitTargets.map((t) => [t.key, rule.targetKeys ? rule.targetKeys.includes(t.key) : true]));
    setEnabled(seeded);
    // Edit clicked before the library loaded → the draft was seeded empty; fill
    // it now so the grid and the Save see the same complete ON/OFF map.
    setTgtDraft((cur) => (Object.keys(cur).length ? cur : seeded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Page-level VIEW/EDIT: the whole Rule Detail is READ-ONLY until Edit is
  // clicked; Save persists and returns to read-only, Cancel discards and returns.
  // Targets follow the same flag — View shows only enabled targets, Edit shows
  // every Permit Library parameter with the saved ON/OFF states on a DRAFT copy.
  const [tgtDraft, setTgtDraft] = useState<Record<string, boolean>>({});
  // HOLD TIME category (RTCFR 2026-10-14): the Targets tab is the Test Method grid
  // (Matrix · Test · Method · Hold Time · Alert · Warn Before) over the rule's saved
  // holdTimeTargets; the Permit Library grid below is untouched for every other
  // category. htSaved = what is stored (View); htDraft = the Edit copy.
  const isHoldTime = isHoldTimeCategory(categoryName);
  // Suppress duplicate firing applies only to permit-limit exceedances — hold-time warnings fire once
  // per threshold by design. Hardcoded to that category's code, RC-01 (owner decision 2026-10-19).
  const ruleCategoryCode = useNcStore((s) => {
    const c = s.categories.find((x) => x.rules.some((r) => r.code === rule.code));
    return c ? (c.codeLabel || `RC-${String(c.code).padStart(2, '0')}`) : undefined;
  });
  const isPermitExceedance = ruleCategoryCode === PERMIT_EXCEEDANCE_CATEGORY_CODE;
  const [htCatalog, setHtCatalog] = useState<HoldTimeCatalogRow[] | null>(null);
  useEffect(() => {
    if (!isHoldTime) return;
    let alive = true;
    getHoldTimeCatalog().then((rows) => { if (alive) setHtCatalog(rows); }).catch(() => { if (alive) setHtCatalog([]); });
    return () => { alive = false; };
  }, [isHoldTime]);
  const [htSaved, setHtSaved] = useState<Record<string, HoldTimeSel>>(() => selFromTargets(rule.holdTimeTargets));
  const [htDraft, setHtDraft] = useState<Record<string, HoldTimeSel>>({});
  const htSavedCount = Object.values(htSaved).filter((v) => v.enabled).length;
  const enabledCount = isHoldTime ? htSavedCount : permitTargets.filter((t) => enabled[t.key]).length;
  const viewTargets = permitTargets.filter((t) => enabled[t.key]);
  // TRIGGER — the workflow forms/events this rule fires on (multi-select).
  // Loaded from the saved rule (falls back to result-validation for legacy rules
  // with none).  A saved trigger now disabled in Default Settings is KEPT, shown
  // as a flagged row, and stays selectable — never silently changed.
  const savedTriggers = rule.triggers?.length ? rule.triggers : ['result-validation'];
  const [triggerSel, setTriggerSel] = useState<Record<string, boolean>>(
    () => Object.fromEntries(savedTriggers.map((id) => [id, true])));
  // Rows: every Default-Settings-enabled form, plus any saved trigger that is now
  // disabled (so it is never dropped from view).
  const triggerRows = useMemo(() => {
    const list = [...enabledTriggerForms()];
    for (const id of Object.keys(triggerSel)) {
      const f = triggerFormById(id);
      if (f && !list.some((x) => x.id === f.id)) list.push(f);
    }
    return list;
  }, [triggerSel]);
  const selectedTriggerIds = triggerRows.filter((f) => triggerSel[f.id]).map((f) => f.id);
  // A saved+selected trigger whose form is disabled in Default Settings.
  const disabledSelected = triggerRows.filter((f) => triggerSel[f.id] && !f.enabled);
  // General governance toggles
  // Requires acknowledgment — AI_NotificationRule.Ack. A rule saved with it OFF arrives as
  // undefined, so read it as a strict boolean (it used to default ON and be re-saved ON).
  const [gov, setGov] = useState({ ack: !!rule.ack });
  // The SAVED value — drives the header chip and the Delivery Method note, so they show what
  // is stored (not the unsaved toggle) and follow a Save without reloading the page.
  const [savedAck, setSavedAck] = useState(!!rule.ack);
  // Delivery / escalation toggles
  // bypass + autoResolve are SAVED (AI_NotificationRule.BypassDigest / AutoResolve, 2026-10-19);
  // suppress and portal are still page-only.
  const [del, setDel] = useState({ suppress: true, bypass: rule.bypassDigest ?? true, portal: false, autoResolve: !!rule.autoResolve });
  // Delay + Business hours are SAVED (AI_NotificationRule.DelayMode / BusinessHoursOnly, 2026-10-19) and the
  // server holds / batches the alerts. Repeat until acknowledged is SAVED too (RepeatEnabled / RepeatEvery /
  // RepeatUnit, 2026-10-20) and the server re-sends the alert as "Reminder N"; it sits in the In-App alert
  // group because acknowledging in the bell is what stops it.
  const [delay, setDelay] = useState<DelayLabel>(DELAY_LABEL[rule.delayMode ?? 'Immediate']);
  const [bizHours, setBizHours] = useState(!!rule.businessHoursOnly);
  // Escalation (SAVED, 2026-10-20): '' = no escalation, else a Recipient Group id.
  const [esc, setEsc] = useState<{ groupId: string; after: NcEscalateAfter }>({
    groupId: rule.escalationGroupId ?? '', after: rule.escalateAfter ?? '1h',
  });
  const [repeat, setRepeat] = useState<{ on: boolean; value: string; unit: RepeatUnit }>({
    on: !!rule.repeatEnabled, value: String(rule.repeatEvery ?? 4), unit: rule.repeatUnit === 'Minutes' ? 'Minutes' : 'Hours',
  });
  // Delivery Method channels that are SAVED on the rule (AI_NotificationRule.NotifyInApp /
  // NotifyEmail). Dashboard / SMS / Teams are not sent by anything yet and stay as before.
  const [chan, setChan] = useState({ inApp: rule.notifyInApp ?? false, email: rule.notifyEmail ?? true });
  // Pop-up setting for this rule's in-app alerts (AI_NotificationRule.PopupSettingId): '' = follow the
  // Default. The list is the named settings from Notification Center → Pop-up Settings.
  const [popupSetting, setPopupSetting] = useState<string>(rule.popupSettingId ?? '');
  const popupSnap = useRef<string>(rule.popupSettingId ?? '');
  const [popupList, setPopupList] = useState<PopupSetting[]>([]);
  useEffect(() => {
    let live = true;
    // Quiet: the dropdown just falls back to "Default" when the list cannot be loaded.
    listPopupSettings().then((l) => { if (live) setPopupList(l); }).catch(() => { if (live) setPopupList([]); });
    return () => { live = false; };
  }, []);
  const popupDefault = popupList.find((p) => p.isDefault);
  /** What this rule's pop-up will actually do: its chosen setting if Active, else the Default. */
  const popupEffective: (AlertPopupSettings & { name: string }) | undefined = (() => {
    const chosen = popupSetting ? popupList.find((p) => p.id === popupSetting && p.isActive) : undefined;
    return chosen ?? popupDefault;
  })();

  // ── Page read-only / edit gate ─────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  // Bumped on Cancel to remount the tab panels so the uncontrolled inputs
  // (delivery / escalation / template selects & checkboxes) revert to defaults.
  const [formKey, setFormKey] = useState(0);
  // Snapshot of the controlled fields taken when Edit starts, restored on Cancel.
  const snapRef = useRef<{
    active: boolean; gov: typeof gov; del: typeof del; chan: typeof chan; triggerSel: typeof triggerSel;
    delay: typeof delay; repeat: typeof repeat; bizHours: boolean; esc: typeof esc;
    name: string; severity?: Severity; recipientGroups: string[]; emailTemplate: string | null;
  } | null>(null);

  function toggleExpand(key: string) {
    setExpanded((cur) => ({ ...cur, [key]: !cur[key] }));
  }
  function toggleDraftTarget(key: string) { setTgtDraft((cur) => ({ ...cur, [key]: !cur[key] })); }

  // Enter edit: snapshot every editable field (deep copy of recipients) and seed
  // the Targets draft so all tabs edit against the current values.
  function startEdit() {
    snapRef.current = {
      active, gov: { ...gov }, del: { ...del }, chan: { ...chan }, triggerSel: { ...triggerSel },
      delay, repeat: { ...repeat }, bizHours, esc: { ...esc },
      name, severity, recipientGroups: [...recipientGroups], emailTemplate,
    };
    popupSnap.current = popupSetting;
    // A COMPLETE map (every library parameter, explicit true/false) — never a
    // sparse copy, or unseeded keys would render ON yet save as OFF.
    setTgtDraft(Object.fromEntries(permitTargets.map((t) => [t.key, enabled[t.key] === true])));
    setHtDraft(Object.fromEntries(Object.entries(htSaved).map(([id, v]) => [id, { enabled: v.enabled, warn: [...v.warn] }])));
    setIsEditing(true);
  }
  // Opened from the list toolbar Edit → enter Edit mode through the SAME startEdit the
  // in-page Edit button uses (same snapshot, so Cancel / Save behave identically). Once,
  // on open, before the first paint (no View-mode flash); later data loads and
  // re-renders never touch isEditing. A Permit Library that arrives afterwards fills the
  // still-empty Targets draft (see the library effect above).
  const openedInEdit = useRef(false);
  useLayoutEffect(() => {
    if (initialMode !== 'edit' || openedInEdit.current) return;
    openedInEdit.current = true;
    startEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Cancel: restore the snapshot, drop the Targets draft, remount the panels so
  // uncontrolled inputs revert, and return to read-only.
  function cancelEdit() {
    const s = snapRef.current;
    if (s) {
      setActive(s.active); setGov(s.gov); setDel(s.del); setChan(s.chan); setTriggerSel(s.triggerSel);
      setDelay(s.delay); setRepeat(s.repeat); setBizHours(s.bizHours); setEsc(s.esc);
      setName(s.name); setSeverity(s.severity); setRecipientGroups([...s.recipientGroups]); setEmailTemplate(s.emailTemplate);
      setPopupSetting(popupSnap.current);
    }
    setTgtDraft({});
    setHtDraft({});
    setIsEditing(false);
    setFormKey((k) => k + 1);
    notify.info('Changes discarded.');
  }

  // Build REAL audit entries from the diff between the pre-edit snapshot and the
  // current values. Returns [] when nothing changed — never mock rows.
  function buildAudit(newKeys: string[], prevKeys: string[]): NcAuditEntry[] {
    const s = snapRef.current;
    if (!s) return [];
    const now = new Date().toISOString();
    const out: NcAuditEntry[] = [];
    const add = (action: string, field?: string, oldV?: string, newV?: string) =>
      out.push({ at: now, user: meName, action, field, oldValue: oldV, newValue: newV });
    if (s.name !== name) add('Renamed', 'Rule Name', s.name, name);
    if (s.severity !== severity) add('Severity changed', 'Severity', s.severity ? SEVERITY_LABEL[s.severity] : '—', severity ? SEVERITY_LABEL[severity] : '—');
    if (s.active !== active) add(active ? 'Enabled' : 'Disabled', 'Status', s.active ? 'Active' : 'Disabled', active ? 'Active' : 'Disabled');
    if (s.gov.ack !== gov.ack) add('Acknowledgment changed', 'Requires acknowledgment', s.gov.ack ? 'Required' : 'Not required', gov.ack ? 'Required' : 'Not required');
    if (s.emailTemplate !== emailTemplate) add('Email template changed', 'Email Template', templateName(s.emailTemplate), templateName(emailTemplate));
    const onOff = (v: boolean) => (v ? 'On' : 'Off');
    if (s.chan.inApp !== chan.inApp) add('Delivery method changed', 'In-App (bell)', onOff(s.chan.inApp), onOff(chan.inApp));
    if (s.chan.email !== chan.email) add('Delivery method changed', 'Email', onOff(s.chan.email), onOff(chan.email));
    if (s.delay !== delay) add('Delay changed', 'Delay', s.delay, delay);
    if (s.repeat.on !== repeat.on || (repeat.on && (s.repeat.value !== repeat.value || s.repeat.unit !== repeat.unit)))
      add('Delivery method changed', 'Repeat until acknowledged', repeatLabel(s.repeat), repeatLabel(repeat));
    if (s.esc.groupId !== esc.groupId) add('Escalation changed', 'Escalate to', escGroupName(s.esc.groupId), escGroupName(esc.groupId));
    if (s.esc.after !== esc.after) add('Escalation changed', 'Escalate after', ESCALATE_AFTER_LABEL[s.esc.after], ESCALATE_AFTER_LABEL[esc.after]);
    if (s.bizHours !== bizHours) add('Business hours changed', 'Business hours', bizHoursLabel(s.bizHours), bizHoursLabel(bizHours));
    if (s.del.bypass !== del.bypass) add('Delivery method changed', 'Bypass digest batching', onOff(s.del.bypass), onOff(del.bypass));
    if (s.del.autoResolve !== del.autoResolve) add('Auto-resolve changed', 'Auto-resolve when condition clears', onOff(s.del.autoResolve), onOff(del.autoResolve));
    const beforeT = triggerRows.filter((f) => s.triggerSel[f.id]).map((f) => f.id);
    const triggersChanged = beforeT.length !== selectedTriggerIds.length || beforeT.some((id) => !selectedTriggerIds.includes(id));
    if (triggersChanged) add('Triggers changed', 'Triggers', triggerLabels(beforeT).join(', ') || '—', triggerLabels(selectedTriggerIds).join(', ') || '—');
    const beforeG = new Set(s.recipientGroups);
    const afterG = new Set(recipientGroups);
    const groupsChanged = beforeG.size !== afterG.size || [...afterG].some((n) => !beforeG.has(n));
    if (groupsChanged) add('Recipient groups changed', 'Recipients', groupNames(s.recipientGroups).join(', ') || '—', groupNames(recipientGroups).join(', ') || '—');
    const targetsChanged = prevKeys.length !== newKeys.length || prevKeys.some((k) => !newKeys.includes(k));
    if (targetsChanged) add('Targets changed', 'Targets', `${prevKeys.length} target${prevKeys.length === 1 ? '' : 's'}`, `${newKeys.length} target${newKeys.length === 1 ? '' : 's'}`);
    // Hold Time targets — one entry per changed (test, hold-time type): Before Prep and
    // Before Analysis are audited separately ("BOD5 / SM 5210B · Before Prep · Alert").
    if (isHoldTime) {
      const names = new Map((htCatalog ?? []).map((c) => [htKey(c.testMethodId, c.holdTimeType), targetLabel(c)]));
      const saved = (sel: Record<string, HoldTimeSel>) => new Map(toSavedTargets(sel, htCatalog ?? [])
        .map((t) => [htKey(t.testMethodId, t.holdTimeType), { enabled: t.enabled, warn: t.warnings }]));
      const before = saved(htSaved);
      const after = saved(htDraft);
      for (const id of new Set([...before.keys(), ...after.keys()])) {
        const stored = rule.holdTimeTargets?.find((t) => htKey(t.testMethodId, t.holdTimeType) === id);
        const label = names.get(id) ?? (stored ? targetLabel(stored) : 'Test');
        const b = before.get(id); const a = after.get(id);
        if (!b || !a) { add(a ? 'Hold Time target added' : 'Hold Time target removed', `${label} · Hold Time Target`, describeTarget(label, b), describeTarget(label, a)); continue; }
        if (b.enabled !== a.enabled) add(a.enabled ? 'Alert enabled' : 'Alert disabled', `${label} · Alert`, b.enabled ? 'On' : 'Off', a.enabled ? 'On' : 'Off');
        if (fmtWarnings(b.warn) !== fmtWarnings(a.warn)) add('Warn Before changed', `${label} · Warn Before`, fmtWarnings(b.warn), fmtWarnings(a.warn));
      }
    }
    return out;
  }

  // Save: confirm, then persist name / severity / enabled / recipients / email
  // template / targets in ONE write, appending the real change history.
  function saveEdit() {
    // Hold Time: at least one Alert on and every Warn Before value valid — checked
    // BEFORE the confirm so the user stays in Edit with every value kept.
    if (isHoldTime) {
      const err = htCatalog === null ? 'Hold-time tests are still loading.' : holdTimeTargetsError(htDraft, htCatalog);
      if (err) { notify.warning(err); setTab('targets'); return; }
    }
    // Repeat until acknowledged: a usable interval when it is ticked (the server checks the same rule).
    if (repeat.on && repeatIntervalError(repeat)) { notify.warning(repeatIntervalError(repeat)!); setTab('delivery'); return; }
    void confirm({ title: 'Save', message: 'Do you want to Save?' }).then((ok) => {
      if (!ok) return;
      // A Hold Time rule has no Permit Library targets (its targets are the tests).
      const newKeys = isHoldTime ? [] : permitTargets.filter((t) => tgtDraft[t.key]).map((t) => t.key);
      const prevKeys = isHoldTime ? [] : permitTargets.filter((t) => enabled[t.key]).map((t) => t.key);
      const htTargets = isHoldTime ? toSavedTargets(htDraft, htCatalog ?? []) : undefined;
      const htOn = htTargets?.filter((t) => t.enabled).length ?? 0;
      const newEntries = buildAudit(newKeys, prevKeys);
      const nextHistory = [...newEntries, ...history];   // newest first
      // Groups persist by ID (recipientGroups refs); the names in config are only
      // the display snapshot — the server links by id, never by name.
      const groupRefs = groupRefsFor(recipientGroups, liveGroups, rule.recipientGroups);
      const liveNames = groupRefs.filter((g) => g.exists).map((g) => g.name);
      // The template persists by ID (emailTemplateId); the name in config is only
      // the display snapshot — the server links by id, never by name.
      const tplName = emailTemplate ? liveTemplates.find((t) => t.id === emailTemplate)?.name : undefined;
      const cfg: NcRuleConfig = { ...parseRuleConfig(rule), recipients: undefined, recipientGroups: liveNames, emailTemplate: tplName, history: nextHistory };
      const cleanName = name.trim() || rule.name;
      // commit local view state
      setName(cleanName);
      setSavedAck(gov.ack);
      setEnabled(tgtDraft);
      if (isHoldTime) setHtSaved(selFromTargets(htTargets));
      setHistory(nextHistory);
      // persist (write-through)
      patchRule(rule.code, {
        name: cleanName,
        severity,
        enabled: active,
        // Requires acknowledgment (AI_NotificationRule.Ack) — was shown but never written.
        ack: gov.ack,
        recipients: liveNames.join(', '),
        recipientGroups: groupRefs,
        emailTemplateId: emailTemplate,
        // Untouched = keep the stored link; changed to "Default" = '' (clears it); changed to a setting = its id.
        popupSettingId: popupSetting === (rule.popupSettingId ?? '') ? (rule.popupSettingId ?? null) : popupSetting,
        targets: isHoldTime
          ? (htOn ? `${htOn} target${htOn === 1 ? '' : 's'}` : undefined)
          : (newKeys.length ? `${newKeys.length} target${newKeys.length === 1 ? '' : 's'}` : undefined),
        targetKeys: newKeys,
        // Hold Time only — every other category leaves it out (stored set untouched).
        ...(htTargets ? { holdTimeTargets: htTargets } : {}),
        // The ticked trigger forms — without this the Triggers tab looked saved but
        // dbo.AI_NotificationRuleTrigger kept the old set, so the rule never fired there.
        triggers: selectedTriggerIds,
        config: JSON.stringify(cfg),
        notifyInApp: chan.inApp,
        notifyEmail: chan.email,
        autoResolve: del.autoResolve,
        delayMode: DELAY_STORED[delay],
        businessHoursOnly: bizHours,
        bypassDigest: del.bypass,
        repeatEnabled: repeat.on,
        repeatEvery: Number(repeat.value) || undefined,
        repeatUnit: repeat.unit,
        // Untouched = keep the stored link; changed to "No escalation" = '' (clears it); else the group id.
        escalationGroupId: esc.groupId === (rule.escalationGroupId ?? '') ? (rule.escalationGroupId ?? null) : esc.groupId,
        escalateAfter: esc.after,
      });
      setIsEditing(false);
      notify.success('Saved successfully.');
    });
  }
  function deleteRule() {
    confirm({ title: 'Delete rule?', message: `Delete “${rule.name}”? This cannot be undone.`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { notify.success('Rule deleted.'); onDeleted(); } });
  }
  const duplicateRule = () => notify.success('Rule duplicated.');
  const refreshRule = () => notify.success('Rule refreshed.');

  // Rule Detail action-bar shortcuts (CLAUDE.md standing rule 14, §18.6) — wired
  // to the real edit-gate handlers: view actions gated to view mode, Save to edit
  // mode, Esc cancels an in-progress edit else navigates back.
  useHotkeys([
    { combo: 'alt+shift+c', label: 'Duplicate', group: 'Rule Detail', order: 1, disabled: isEditing, run: duplicateRule },
    { combo: 'alt+e', label: 'Edit', group: 'Rule Detail', order: 2, disabled: isEditing, run: startEdit },
    { combo: 'alt+d', label: 'Delete', group: 'Rule Detail', order: 3, disabled: isEditing, run: deleteRule },
    { combo: 'alt+r', label: 'Refresh', group: 'Rule Detail', order: 4, disabled: isEditing, run: refreshRule },
    { combo: 'mod+s', label: 'Save', group: 'Rule Detail', order: 5, disabled: !isEditing, run: saveEdit },
    { combo: 'esc', label: isEditing ? 'Cancel' : 'Back', group: 'Rule Detail', order: 6, allowInInput: false, run: () => (isEditing ? cancelEdit() : onBack()) },
  ]);

  // Repeat until acknowledged needs In-App + Requires acknowledgment (acknowledging stops it) and is
  // not offered with a Daily digest (a digest goes out once a day by design). Null = available.
  const repeatOff: string | null = !chan.inApp ? 'Needs In-App (bell) ticked above — repeats stop when someone acknowledges in the bell.'
    : !gov.ack ? 'Needs Requires acknowledgment on — acknowledging is what stops the repeats.'
    : delay === 'Daily digest' ? 'Not available with a Daily digest delay — a digest is sent once a day.'
    : null;
  const repeatInvalid = repeat.on && !repeatOff && !!repeatIntervalError(repeat);
  // Escalation needs the same as Repeat: acknowledging is the evidence someone saw the alert. Null = available.
  const escOff: string | null = !chan.inApp ? 'Needs In-App (bell) ticked on the Delivery Method tab — escalation is stopped by an acknowledgment in the bell.'
    : !gov.ack ? 'Needs Requires acknowledgment on (Delivery Method tab) — an alert that needs no sign-off is never escalated.'
    : delay === 'Daily digest' ? 'Not available with a Daily digest delay.'
    : null;
  function escGroupName(id: string): string {
    if (!id) return 'No escalation';
    return liveGroups.find((g) => g.id === id)?.name ?? '(deleted group)';
  }

  return (
    <div className="nc-root">
      <div className="nc-topbar">
        <div className="nc-title-wrap">
          <div className="nc-title-ic"><i className="ti ti-adjustments-horizontal" /></div>
          <div><div className="nc-title">Rule Detail</div><div className="nc-sub">{name} · {rule.code}</div></div>
          <span className="nc-load"><i className="ti ti-bolt" /> Loaded in 0.3 sec</span>
        </div>
        <div className="nc-actions">
          {isEditing ? (
            <>
              <button className="ca-btn" onClick={cancelEdit} title={`Cancel (${formatCombo('esc')})`}><i className="ti ti-x" /> Cancel</button>
              <button className="ca-btn ca-btn-solid" title={`Save (${formatCombo('mod+s')})`} onClick={saveEdit}><span className="tb-i">💾</span> Save</button>
            </>
          ) : (
            <>
              <button className="ca-btn" onClick={duplicateRule} title={`Duplicate (${formatCombo('alt+shift+c')})`}><i className="ti ti-copy" /> Duplicate</button>
              <button className="ca-btn" onClick={startEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
              <button className="ca-btn" onClick={deleteRule} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
              <button className="ca-btn" onClick={refreshRule} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
            </>
          )}
        </div>
      </div>

      <div className="nc-scroll">
        {/* detail header */}
        <div className="nc-dhead">
          <div className="nc-dcrumb" onClick={onBack}><i className="ti ti-arrow-left" /> Notification Rules</div>
          <div className="nc-dhead-top">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {rule.outbound ? <span className="nc-comm"><span className="dot" />Outbound</span> : severity ? <SevPill s={severity} /> : null}
                {savedAck && <span className="nc-acktag">Ack required</span>}
              </div>
              <div className="nc-dname">{name}</div>
            </div>
            <div className="nc-dstatus">
              <span className={`nc-toggle${active ? ' on' : ''}${isEditing ? '' : ' nc-ro'}`} role="button" onClick={isEditing ? () => setActive(!active) : undefined} />
              <div><div className="nc-dsb-label">{active ? 'Active' : 'Disabled'}</div><div className="nc-dsb-sub">{active ? 'Evaluating live' : 'Not evaluating'}</div></div>
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className="nc-tabs">
          {TABS.map((t) => (
            <div key={t.key} className={`nc-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
              <i className={`ti ${t.icon}`} /> {t.label}
              {t.key === 'targets' && <span className="nc-tab-count">{enabledCount}</span>}
              {t.key === 'recipients' && <span className="nc-tab-count">{recipientGroups.length}</span>}
            </div>
          ))}
        </div>

        <Fragment key={formKey}>
        {/* GENERAL */}
        {tab === 'general' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-info-circle" /> Rule Definition</div></div>
              <div className="nc-panel-body">
                <div className="nc-infogrid">
                  <Info label="Rule Name">{isEditing
                    ? <input className="nc-finput" value={name} onChange={(e) => setName(e.target.value)} placeholder="Rule name" />
                    : name}</Info>
                  <Info label="Rule Code"><span className="nc-mono">{rule.code}</span></Info>
                  <Info label="Category">{categoryName}</Info>
                  <Info label="Notification Purpose"><span className={`nc-pp ${rule.purpose}`}>{PURPOSE_LABEL[rule.purpose]}</span></Info>
                  <Info label="Severity">{rule.outbound
                    ? <span className="nc-comm"><span className="dot" />Outbound</span>
                    : isEditing
                      ? <select className="nc-fselect nc-sm" value={severity ?? ''} onChange={(e) => setSeverity(e.target.value as Severity)}>
                          {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>)}
                        </select>
                      : severity ? <SevPill s={severity} /> : <span className="nc-comm"><span className="dot" />Outbound</span>}</Info>
                </div>
              </div>
            </div>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-file-description" /> Description</div></div>
              <div className="nc-panel-body" style={{ fontSize: 12.5, color: 'var(--nc-text-mid)', lineHeight: 1.6 }}>
                Fires when any validated analytical result meets or exceeds the client-specific permit limit (or the configured early-warning fraction) for the monitored analyte. Required acknowledgment ensures a responsible official reviews before report release.
              </div>
            </div>
          </>
        )}

        {/* TRIGGER */}
        {tab === 'trigger' && (
          <div className="nc-panel">
            <div className="nc-panel-body">
              {disabledSelected.length > 0 && (
                <div className="nc-form-hint" style={{ color: 'var(--nc-warn)', marginTop: 0, marginBottom: 12 }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />
                  {disabledSelected.length === 1 ? 'A saved trigger' : 'Saved triggers'} <strong>{disabledSelected.map((f) => f.label).join(', ')}</strong> {disabledSelected.length === 1 ? 'is' : 'are'} currently <strong>disabled</strong> in Default Settings — that event will not fire. The rule's saved configuration is kept unchanged; re-enable the form in Default Settings, or turn it off here and Save.
                </div>
              )}
              <div className="nc-form-group">
                <label className="nc-flabel">Trigger forms / events</label>
                <div className="nc-form-hint" style={{ margin: '0 0 8px' }}>Turn on one or more. The rule evaluates when the applicable record reaches <strong>any</strong> enabled event (once per record — duplicates suppressed).</div>
                <table className="nc-tgt-table">
                  <thead><tr><th>Form Name</th><th style={{ width: 90, textAlign: 'center' }}>Enabled</th></tr></thead>
                  <tbody>
                    {triggerRows.map((f) => (
                      <tr key={f.id}>
                        <td><div className="nc-tgt-name"><i className={`ti ${f.icon}`} style={{ marginRight: 6, color: 'var(--nc-text-mid)' }} />{f.label}{!f.enabled && <span className="nc-oc-off"> · disabled</span>}</div><div className="nc-tgt-scope">{f.sub}</div></td>
                        <td style={{ textAlign: 'center' }}>
                          {/* Enabled changes ONLY from the toggle itself — the row is not clickable. */}
                          <span className={`nc-toggle${triggerSel[f.id] ? ' on' : ''}${isEditing ? '' : ' nc-ro'}`} role="button" aria-pressed={!!triggerSel[f.id]}
                            onClick={isEditing ? () => setTriggerSel((cur) => ({ ...cur, [f.id]: !cur[f.id] })) : undefined} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="nc-infogrid">
                <Info label="Watches">Analysis Result</Info>
                <Info label="Triggers">{selectedTriggerIds.length ? triggerRows.filter((f) => triggerSel[f.id]).map((f) => f.label).join(', ') : '—'}</Info>
                <Info label="Match mode">Any target matches (OR)</Info>
              </div>
              <div className="nc-form-hint" style={{ marginTop: 12 }}><i className="ti ti-bolt" style={{ color: 'var(--nc-orange)', marginRight: 4 }} />{triggersEvaluationText(selectedTriggerIds)}</div>
            </div>
          </div>
        )}

        {/* TARGETS — Permit Library master/detail grid (View = enabled only; Edit = all) */}
        {tab === 'targets' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd">
                <div>
                  <div className="nc-panel-title"><i className="ti ti-target" /> Monitored Targets</div>
                  <div className="nc-panel-sub">{isHoldTime
                    ? (isEditing
                      ? <>Turn <strong>Alert</strong> on for each test to monitor and set its <strong>Warn Before</strong> times. Every test with a configured hold time is listed. Save to apply, Cancel to discard.</>
                      : <>The tests this rule monitors, with their hold time and Warn Before times. Click <strong>Edit</strong> to change them.</>)
                    : isEditing
                    ? <>Turn parameters on or off. Every parameter from the <strong>Permit Library</strong> is listed — only enabled ones are monitored. Save to apply, Cancel to discard.</>
                    : <>The parameters this rule monitors. Expand a row to see its configured Permit Library limits. Click <strong>Edit</strong> to change the selection.</>}
                  </div>
                </div>
                {!isHoldTime && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--nc-text-light)' }}>Match mode:</span>
                  <select className="nc-fselect nc-sm" disabled={!isEditing}><option>Any target matches (OR)</option><option>All targets match (AND)</option></select>
                </div>}
              </div>
              {isHoldTime ? (
                <HoldTimeTargetsGrid
                  catalog={htCatalog ?? []}
                  saved={rule.holdTimeTargets}
                  loading={htCatalog === null}
                  sel={isEditing ? htDraft : htSaved}
                  readOnly={!isEditing}
                  onChange={(id, next) => setHtDraft((cur) => ({ ...cur, [id]: next }))}
                />
              ) : targetsLoading ? (
                <div className="nc-state-row"><span className="nc-inline-spin" /> Loading Permit Library parameters…</div>
              ) : isEditing ? (
                <PermitTargetsGrid
                  targets={permitTargets}
                  enabled={tgtDraft}
                  onToggle={(key) => toggleDraftTarget(key)}
                  expanded={expanded}
                  onExpand={toggleExpand}
                  showSearch
                />
              ) : (
                <PermitTargetsGrid
                  targets={viewTargets}
                  enabled={enabled}
                  onToggle={() => {}}
                  expanded={expanded}
                  onExpand={toggleExpand}
                  readOnly
                  emptyText="No targets enabled — click Edit to choose parameters to monitor."
                />
              )}
            </div>
            <div className="nc-form-hint" style={{ padding: '0 4px' }}>{isHoldTime
              ? <>Hold times read live from each test's Test Guide — Before Prep and Before Analysis separately. Each Warn Before time (days, hours or minutes) must be less than its own hold time.</>
              : isEditing
              ? <>Limits read live from the <strong>Permit Library</strong> — Limit Type, Operator, Limit, Units and effective dates are never re-entered here. A parameter with several limit types (e.g. Fluoride MCL + SMCL) appears once; expand it to see all.</>
              : <>Showing the <strong>{enabledCount}</strong> enabled of {permitTargets.length} available Permit Library parameters. Limits read live from the Permit Library.</>}
            </div>
          </>
        )}

        {/* RECIPIENTS */}
        {tab === 'recipients' && (
          <div className="nc-panel">
            <div className="nc-panel-hd">
              <div><div className="nc-panel-title"><i className="ti ti-users-group" /> Recipients</div><div className="nc-panel-sub"><strong>The setting you configure for this lab.</strong> Which <strong>Recipient Groups</strong> are notified when this rule fires — each group's roles, users and channels resolve from its own configuration.{isEditing ? ' Check a group to add it, uncheck to remove.' : ' Click Edit to change.'}</div></div>
            </div>
            <div className="nc-panel-body">
              {/* Recipient GROUP picker — identical here and in the New Rule
                  wizard's Recipients step. Groups load live from the DB. */}
              <RecipientGroupPicker selected={recipientGroups} onChange={setRecipientGroups} readOnly={!isEditing} known={rule.recipientGroups} />
            </div>
          </div>
        )}

        {/* DELIVERY */}
        {tab === 'delivery' && (
          <div className="nc-panel nc-delivery-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-send" /> Delivery Method</div><div className="nc-panel-sub">How and when the notification is delivered.</div></div></div>
            <div className="nc-panel-body">
              <div className="nc-form-group">
                <label className="nc-flabel">Notification type</label>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  {/* Dashboard is display-only, as before — nothing sends it yet. */}
                  <label className="nc-check"><input type="checkbox" defaultChecked disabled={!isEditing} /> Dashboard</label>
                  {/* In-App + Email are SAVED and drive the dispatcher. */}
                  <label className="nc-check"><input type="checkbox" checked={chan.inApp} disabled={!isEditing}
                    onChange={(e) => setChan((c) => ({ ...c, inApp: e.target.checked }))} /> In-App (bell)</label>
                  <label className="nc-check"><input type="checkbox" checked={chan.email} disabled={!isEditing}
                    onChange={(e) => setChan((c) => ({ ...c, email: e.target.checked }))} /> Email</label>
                  {['SMS', 'Teams'].map((c) => <label key={c} className="nc-check"><input type="checkbox" disabled={!isEditing} /> {c}</label>)}
                </div>
                {!chan.inApp && !chan.email && (
                  <div className="nc-panel-sub" style={{ color: 'var(--ai-notify-error)', marginTop: 6 }}>
                    Neither In-App nor Email is ticked — this rule will record alerts but send nothing.
                  </div>
                )}
              </div>
              {/* In-App alert group (owner request 2026-10-18): the two settings that only affect the In-App
                  alert sit together, are always shown, and are enabled only while In-App (bell) is ticked. */}
              <div className={`nc-inapp-group${chan.inApp ? '' : ' is-off'}`} role="group" aria-label="In-App alert">
                <div className="nc-inapp-hd">
                  <i className="ti ti-bell" /> In-App alert
                  {!chan.inApp && <span className="nc-inapp-off">Turn on In-App (bell) above to use these settings.</span>}
                </div>
                <div className="nc-form-group nc-inapp-popup">
                  <label className="nc-flabel" htmlFor="nc-popup-setting">Pop-up setting</label>
                  {/* Compact dropdown with its description beside it on the same line (owner request 2026-10-18). */}
                  <div className="nc-inapp-popup-row">
                  <select id="nc-popup-setting" className="nc-fselect nc-inapp-popup-select" value={popupSetting} disabled={!isEditing || !chan.inApp}
                    title={chan.inApp ? undefined : 'Needs In-App (bell) — the pop-up setting is only for In-App alerts.'}
                    onChange={(e) => setPopupSetting(e.target.value)}>
                    {/* '' = follow the Default setting — shown by its own name (no "Default" prefix,
                        owner request 2026-10-18); the built-in look when no Default exists. */}
                    <option value="">{popupDefault ? popupDefault.name : 'Built-in (bottom right · auto-close 8 s)'}</option>
                    {popupList.filter((p) => !p.isDefault && (p.isActive || p.id === popupSetting)).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}{p.isActive ? '' : ' (inactive)'}</option>
                    ))}
                  </select>
                  <div className="nc-panel-sub nc-inapp-popup-desc">
                    <i className="ti ti-bell-cog" /> {popupEffective ? describeAlertPopup(popupEffective) : 'Bottom right · auto-close 8 s'}
                    {gov.ack && ' · this rule is ACK REQUIRED, so it always needs Acknowledge'}
                  </div>
                  </div>
                </div>
                {/* Requires acknowledgment (AI_NotificationRule.Ack) — moved here from the General tab
                    (2026-10-16): it only changes how the In-App alert behaves. */}
                <ToggleRow text="Requires acknowledgment" sub={chan.inApp
                    ? 'In-App alerts from this rule stay on screen until someone clicks Acknowledge, pop up again at sign-in until acknowledged, and each acknowledgment is recorded in the audit trail. Emails are not affected.'
                    : 'Needs In-App (bell) ticked above — acknowledgment is only for In-App alerts. Emails cannot be acknowledged.'} on={gov.ack} onClick={() => setGov({ ...gov, ack: !gov.ack })} disabled={!isEditing || !chan.inApp} title={chan.inApp ? undefined : 'Needs In-App (bell) — acknowledgment is only for In-App alerts.'} />
                {/* Repeat every N Hours/Minutes until acknowledged — enabled only with In-App + Requires
                    acknowledgment (the acknowledgment stops it) and not with a Daily digest delay. */}
                <div className={`nc-repeat${repeatOff ? ' is-off' : ''}`} title={repeatOff ?? undefined}>
                  <div className="nc-repeat-row">
                    <label className="nc-check nc-repeat-check">
                      <input type="checkbox" checked={repeat.on} disabled={!isEditing || !!repeatOff}
                        onChange={(e) => setRepeat((r) => ({ ...r, on: e.target.checked }))} />
                      Repeat every
                    </label>
                    <input className={`nc-finput nc-repeat-num${repeatInvalid ? ' invalid' : ''}`} inputMode="numeric" maxLength={3}
                      aria-label="Repeat interval" aria-invalid={repeatInvalid || undefined}
                      value={repeat.value} disabled={!isEditing || !!repeatOff || !repeat.on}
                      onChange={(e) => { if (/^\d*$/.test(e.target.value)) setRepeat((r) => ({ ...r, value: e.target.value })); }} />
                    <UnitSpin value={repeat.unit} disabled={!isEditing || !!repeatOff || !repeat.on}
                      onChange={(u) => setRepeat((r) => ({ ...r, unit: u }))} />
                    <span className="nc-repeat-tail">until acknowledged</span>
                  </div>
                  <div className="nc-togrow-sub">{repeatOff
                    ?? 'Re-sends the alert as “Reminder N” (In-App, and Email when ticked) at this interval until anyone acknowledges it in the bell or it auto-resolves.'}</div>
                  {repeatInvalid && <div className="nc-repeat-err" role="alert">{repeatIntervalError(repeat)}</div>}
                </div>
              </div>
              <div className="nc-form-row" style={{ marginBottom: 16 }}>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel" htmlFor="nc-delay">Delay</label><select id="nc-delay" className="nc-fselect" disabled={!isEditing} value={delay} onChange={(e) => setDelay(e.target.value as typeof delay)}><option>Immediate</option><option>After 1 hour</option><option>Daily digest</option></select></div>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel" htmlFor="nc-bizhours">Business hours</label><select id="nc-bizhours" className="nc-fselect" disabled={!isEditing} value={bizHours ? 'only' : 'any'} onChange={(e) => setBizHours(e.target.value === 'only')}><option value="any">{bizHoursLabel(false)}</option><option value="only">{bizHoursLabel(true)}</option></select></div>
              </div>
              <div className="nc-panel-sub nc-delay-note">{delayNote(delay, bizHours)}</div>
              {/* Permit-limit exceedances only (category RC-01): a hold-time threshold is one occurrence by design. */}
              {isPermitExceedance && (
                <ToggleRow text="Suppress duplicate firing" sub="Re-occurrence on the same source record increments a repeat counter (×N) instead of sending again." on={del.suppress} onClick={() => setDel({ ...del, suppress: !del.suppress })} disabled={!isEditing} />
              )}
              {/* Bypass digest batching only means something with a Daily digest delay — greyed out otherwise
                  (owner decision 2026-10-19); its value is kept, so it applies again if Daily digest is chosen. */}
              <ToggleRow text="Bypass digest batching"
                sub={delay !== 'Daily digest'
                  ? 'Only applies when Delay is Daily digest.'
                  : severity === 'critical'
                    ? 'This Critical rule sends at once, skipping the daily digest and business hours.'
                    : 'Applies to Critical rules only, so it has no effect while this rule is not Critical.'}
                on={del.bypass} onClick={() => setDel({ ...del, bypass: !del.bypass })}
                disabled={!isEditing || delay !== 'Daily digest'}
                title={delay === 'Daily digest' ? undefined : 'Only applies when Delay is Daily digest.'} />
              <ToggleRow text="Client visible (portal)" sub="Whether the client portal can see this notification. Off for internal-only alerts." on={del.portal} onClick={() => setDel({ ...del, portal: !del.portal })} disabled={!isEditing} />
              <ToggleRow text="Auto-resolve when condition clears" sub="When everything the alert covers no longer meets its condition (a permit result back inside the limit or rolled back; a hold-time test prepped or analysed), the alert is marked Resolved: it stops waiting for acknowledgment and the Delivery Log shows when." on={del.autoResolve} onClick={() => setDel({ ...del, autoResolve: !del.autoResolve })} disabled={!isEditing} />
            </div>
          </div>
        )}

        {/* ESCALATION */}
        {tab === 'escalation' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-arrow-up-circle" /> Escalation</div><div className="nc-panel-sub">If nobody acknowledges an alert in time, send it once more — as “Escalated: …” — to a higher-level group.</div></div></div>
            <div className={`nc-panel-body${escOff ? ' nc-esc-off' : ''}`} title={escOff ?? undefined}>
              {escOff && <div className="nc-panel-sub nc-esc-reason">{escOff}</div>}
              <div className="nc-form-row" style={{ marginBottom: 12 }}>
                <div className="nc-form-group" style={{ marginBottom: 0 }}>
                  <label className="nc-flabel" htmlFor="nc-esc-group">Escalate to</label>
                  <select id="nc-esc-group" className="nc-fselect" disabled={!isEditing || !!escOff} value={esc.groupId}
                    onChange={(e) => setEsc({ ...esc, groupId: e.target.value })}>
                    <option value="">No escalation</option>
                    {liveGroups.filter((g) => g.id).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    {esc.groupId && !liveGroups.some((g) => g.id === esc.groupId) && <option value={esc.groupId}>(deleted group)</option>}
                  </select>
                </div>
                <div className="nc-form-group" style={{ marginBottom: 0 }}>
                  <label className="nc-flabel" htmlFor="nc-esc-after">Escalate after</label>
                  <select id="nc-esc-after" className="nc-fselect" disabled={!isEditing || !!escOff || !esc.groupId} value={esc.after}
                    onChange={(e) => setEsc({ ...esc, after: e.target.value as NcEscalateAfter })}>
                    {(Object.keys(ESCALATE_AFTER_LABEL) as NcEscalateAfter[]).map((k) => <option key={k} value={k}>{ESCALATE_AFTER_LABEL[k]}</option>)}
                  </select>
                </div>
              </div>
              <div className="nc-panel-sub">{escNote(esc, bizHours)}</div>
            </div>
          </div>
        )}

        {/* TEMPLATE */}
        {tab === 'template' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-mail" /> Email Template</div><div className="nc-panel-sub">The template used to render this rule's email. Manage templates on the Email Templates screen.</div></div></div>
            <div className="nc-panel-body">
              <EmailTemplatePicker value={emailTemplate} onChange={setEmailTemplate} readOnly={!isEditing} />
            </div>
          </div>
        )}

        {/* AUDIT — real persisted change history (config.history); no mock rows. */}
        {tab === 'audit' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-history" /> Configuration Audit</div><div className="nc-panel-sub">Read-only change history for this rule — recorded on Save.</div></div></div>
            {history.length === 0 ? (
              <div className="nc-panel-body"><div className="nc-form-hint" style={{ margin: 0 }}>No changes recorded yet. Edits made here (rule name, severity, recipients, targets, email template, enable / disable) are logged after Save.</div></div>
            ) : (
              <table className="nc-tbl" style={{ border: 'none' }}>
                <thead><tr><th>When</th><th>User</th><th>Action</th><th>Field</th><th>Old</th><th>New</th></tr></thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i}>
                      <td className="nc-mono">{fmtLocalShort(h.at)}</td>
                      <td>{h.user}</td>
                      <td>{h.action}</td>
                      <td>{h.field ?? '—'}</td>
                      <td>{h.oldValue ?? '—'}</td>
                      <td>{h.newValue ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        </Fragment>
      </div>
    </div>
  );
}
